import { useEffect, useMemo, useState } from 'react';

import { api } from './api';
import { CloseIcon, ReturnIcon } from './icons';
import { productSecondaryText } from './product-display';

interface ReturnLine {
  id: string;
  productName: string;
  detailName: string;
  unitName?: string;
  sku?: string;
  quantity: number;
  availableQuantity: number;
  stockCount: number;
  price: number;
  isGift?: boolean;
}

interface ReturnDetailResponse {
  invoice: {
    id: string;
    sku: string;
    partnerName?: string;
    updatedAt: string;
    is_lock?: number;
    status?: number;
  };
  lines: ReturnLine[];
}

function money(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(Number(value) || 0);
}

export function InboundReturnModal({
  invoiceId,
  onClose,
  onReturned
}: {
  invoiceId: string;
  onClose: () => void;
  onReturned: (message: string) => void;
}) {
  const [data, setData] = useState<ReturnDetailResponse | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    api<ReturnDetailResponse>(`/api/invoices/inbound/${invoiceId}`, {}, controller.signal)
      .then(setData)
      .catch((reason) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Không thể tải sản phẩm có thể trả.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [invoiceId]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [onClose, saving]);

  const selected = useMemo(() => Object.values(quantities).filter((quantity) => quantity > 0).length, [quantities]);
  const estimatedRefund = useMemo(() => (data?.lines ?? []).reduce((sum, line) => {
    const quantity = quantities[line.id] ?? 0;
    return sum + (line.isGift ? 0 : Math.round(quantity * Number(line.price)));
  }, 0), [data, quantities]);

  function maximum(line: ReturnLine): number {
    return Math.max(0, Math.min(Number(line.quantity), Number(line.availableQuantity)));
  }

  async function submit() {
    if (!data || selected === 0) {
      setError('Vui lòng nhập số lượng trả cho ít nhất một sản phẩm.');
      return;
    }
    const lines = data.lines
      .map((line) => ({ detailId: line.id, quantity: quantities[line.id] ?? 0, max: maximum(line) }))
      .filter((line) => line.quantity > 0);
    const invalid = lines.find((line) => !Number.isInteger(line.quantity) || line.quantity > line.max);
    if (invalid) {
      setError('Số lượng trả phải là số nguyên và không vượt tồn kho khả dụng.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await api<{ result: { message: string } }>(`/api/invoices/inbound/${invoiceId}/returns`, {
        method: 'POST',
        body: JSON.stringify({
          updatedAt: data.invoice.updatedAt,
          lines: lines.map(({ detailId, quantity }) => ({ detailId, quantity }))
        })
      });
      onReturned(result.result.message);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Không thể trả hàng.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="drawer-backdrop" role="presentation">
      <section className="invoice-return-modal" role="dialog" aria-modal="true" aria-labelledby="return-title">
        <header className="drawer-header">
          <h2 id="return-title">Trả hàng nhà phân phối</h2>
          <button type="button" className="icon-button" onClick={onClose} disabled={saving} aria-label="Đóng"><CloseIcon /></button>
        </header>
        {loading ? <div className="editor-loading"><span className="spinner" /> Đang tải…</div> : null}
        {data ? (
          <div className="return-layout">
            <section className="return-meta">
              <div><span>Hóa đơn</span><strong>{data.invoice.sku}</strong></div>
              <div><span>Nhà phân phối</span><strong>{data.invoice.partnerName || 'Không xác định'}</strong></div>
              <div><span>Dòng đã chọn</span><strong>{selected}</strong></div>
              <div><span>Giá trị hàng trả dự kiến</span><strong>{money(estimatedRefund)} ₫</strong></div>
            </section>
            <div className="return-lines-wrap">
              <table className="return-lines">
                <thead><tr><th>Sản phẩm / lô</th><th>Đã nhập</th><th>Tồn hiện tại</th><th>ĐVT</th><th>Giá nhập</th><th>Số lượng trả</th></tr></thead>
                <tbody>{data.lines.map((line) => {
                  const max = maximum(line);
                  const disabled = max < 1 || Number(line.stockCount) !== 1;
                  return (
                    <tr key={line.id}>
                      <td><strong>{line.productName}</strong><span>{productSecondaryText(line.productName, line.detailName, line.sku || 'Không có mã lô', line.isGift ? 'Quà tặng' : '')}</span></td>
                      <td className="align-right">{money(line.quantity)}</td>
                      <td className="align-right">{money(line.availableQuantity)}</td>
                      <td>{line.unitName || '—'}</td>
                      <td className="align-right">{money(line.price)} ₫</td>
                      <td><input type="number" min={0} max={max} step={1} value={quantities[line.id] ?? 0} disabled={disabled || saving} aria-label={`Số lượng trả ${line.productName}`} title={Number(line.stockCount) !== 1 ? 'Lô có dữ liệu tồn kho không hợp lệ' : undefined} onChange={(event) => { const quantity = Math.max(0, Math.min(max, Math.trunc(Number(event.target.value) || 0))); setQuantities((current) => ({ ...current, [line.id]: quantity })); setError(''); }} /></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
            {error ? <div className="return-error" role="alert">{error}</div> : null}
          </div>
        ) : error ? <div className="detail-error" role="alert">{error}</div> : null}
        <footer className="drawer-footer">
          <button type="button" className="secondary-action" onClick={onClose} disabled={saving}>Hủy</button>
          <button type="button" className="primary-action" onClick={() => void submit()} disabled={saving || loading || selected === 0}>{saving ? <span className="button-spinner" /> : <ReturnIcon />} {saving ? 'Đang xử lý…' : 'Xác nhận trả hàng'}</button>
        </footer>
      </section>
    </div>
  );
}
